import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import { useBillingPermissions } from "./ipd-billing/useBillingPermissions";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  listApprovalRequests,
  approveReceiptCancel,
  executeReceiptCancel,
  approveInvoiceCancel,
  executeInvoiceCancel,
  approvePaymentRefund,
  approvePrintReset,
  executePrintReset,
  decideBackdatedCharge,
  decideDiscountApproval,
  toBillingError,
  BILLING_PERMS,
} from "./ipd-billing/billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Approvals inbox (spec Part B2). Patient/admission-scoped, because approve &
// execute are admission-scoped endpoints — `admission_id` is the selected patient.
// Request → Approve / Reject → Execute.
// ─────────────────────────────────────────────────────────────────────────────

// Billing manager. Discounts are manager-only and carry no EMR permission code.
const MANAGER_ROLE_LEVEL = 3;

const FILTERS = [
  { id: "PENDING", label: "Pending" },
  { id: "APPROVED", label: "Approved" },
  { id: "DONE", label: "Executed" },
  { id: "REJECTED", label: "Rejected" },
  { id: "ALL", label: "All" },
];

// approval_context → how to render + which admission-scoped calls to use.
const CONTEXT_META = {
  RECEIPT_CANCEL: {
    kind: "RECEIPT", label: "Receipt cancel",
    approve: approveReceiptCancel, execute: executeReceiptCancel,
    approvePerm: BILLING_PERMS.RECEIPT_CANCEL_APPROVE, execPerm: BILLING_PERMS.RECEIPT_CANCEL_EXECUTE,
  },
  INVOICE_CANCEL: {
    kind: "INVOICE", label: "Invoice cancel",
    approve: approveInvoiceCancel, execute: executeInvoiceCancel,
    approvePerm: BILLING_PERMS.INVOICE_CANCEL_APPROVE, execPerm: BILLING_PERMS.INVOICE_CANCEL_EXECUTE,
  },
  PAYMENT_REFUND: {
    kind: "REFUND", label: "Refund",
    approve: approvePaymentRefund, execute: null,
    approvePerm: BILLING_PERMS.PAYMENT_REFUND_APPROVE, execPerm: null,
    postApproveHint: "Create refund instruction at the counter",
  },
  PRINT_COUNTER_RESET: {
    kind: "PRINT_RESET", label: "Print reset",
    approve: approvePrintReset, execute: executePrintReset,
    approvePerm: BILLING_PERMS.PRINT_COUNTER_RESET_APPROVE, execPerm: BILLING_PERMS.PRINT_COUNTER_RESET_EXECUTE,
  },
  // A discount is approved outright — there is no execute step, so an approved discount is
  // finished and the row never reaches the Approved bucket needing a second action.
  // Gated on role LEVEL, not a permission code: ADHOC_DISCOUNT_APPROVE lives only in the
  // billing-side tables, so the EMR permission map this screen reads carries no discount
  // code at all and `can()` would answer no for everyone, manager included.
  // A backdated charge is approved outright - approving is what posts it to the bill, so
  // there is no execute step. Gated on role LEVEL for the same reason as the discount below:
  // BACKDATED_CHARGE_APPROVE lives only in the billing-side tables.
  BACKDATED_CHARGE: {
    kind: "BACKDATED", label: "Backdated charge",
    approve: decideBackdatedCharge, execute: null,
    approvePerm: null, execPerm: null, approveLevel: MANAGER_ROLE_LEVEL,
    // the policy sets require_reason, so an empty note is rejected upstream
    requireReason: true,
  },
  ADHOC_DISCOUNT: {
    kind: "DISCOUNT", label: "Adhoc discount",
    approve: decideDiscountApproval, execute: null,
    approvePerm: null, execPerm: null, approveLevel: MANAGER_ROLE_LEVEL,
    // the discount policy sets require_reason, so an empty approve note is rejected upstream
    requireReason: true,
  },
};

function metaFor(context) {
  const c = String(context || "").toUpperCase();
  if (CONTEXT_META[c]) return CONTEXT_META[c];
  if (c.includes("RECEIPT")) return CONTEXT_META.RECEIPT_CANCEL;
  if (c.includes("INVOICE")) return CONTEXT_META.INVOICE_CANCEL;
  if (c.includes("REFUND")) return CONTEXT_META.PAYMENT_REFUND;
  if (c.includes("PRINT")) return CONTEXT_META.PRINT_COUNTER_RESET;
  if (c.includes("DISCOUNT")) return CONTEXT_META.ADHOC_DISCOUNT;
  if (c.includes("BACKDAT")) return CONTEXT_META.BACKDATED_CHARGE;
  return null;
}

// Normalize a Part B2 approval-request row.
function normalizeApproval(raw, fallbackAdmissionId) {
  const context = String(raw.approval_context || raw.context || "").toUpperCase();
  const meta = metaFor(context);
  return {
    context,
    label: meta?.label || (context ? context.replace(/_/g, " ").toLowerCase() : "Request"),
    meta,
    // Refund approve/execute are keyed by refund_id (= workflow_detail_id);
    // cancel / invoice / print-reset are keyed by approval_request_id.
    id: meta?.kind === "REFUND"
      ? (raw.refund_id || raw.workflow_detail_id || raw.approval_request_id)
      : (raw.approval_request_id || raw.request_id || raw.id),
    workflowId: raw.workflow_detail_id || null,
    // Approve/execute are admission-scoped, so each tenant-wide row must carry its
    // admission id. It can arrive top-level or nested under `metadata` (see UI plan);
    // print-reset rows use the nested form, which is why they showed "Open patient to act".
    admissionId: String(
      raw.emr_admission_id ??
        raw.admission_id ??
        raw.metadata?.emr_admission_id ??
        raw.metadata?.admission_id ??
        raw.ipd_admission_id ??
        fallbackAdmissionId ??
        ""
    ),
    // The tenant-wide feed names these in snake_case; normalize them here so the table
    // reads one shape. They were previously rendered straight off the raw row, which
    // silently produced blank Admission Id and Patient columns.
    patientName: raw.patient_name || raw.patientName || "",
    patientCode: raw.patient_id || "",
    number: raw.request_number || "—",
    reference: raw.receipt_number || raw.invoice_number || raw.target_entity_id || "—",
    amount: Number(raw.requested_amount ?? raw.amount ?? 0),
    reason: raw.request_reason || raw.reason || "",
    postCancelAction: String(raw.post_cancel_action || "").toUpperCase(),
    status: String(raw.request_status || raw.status || "PENDING").toUpperCase(),
    uiStatusLabel: raw.ui_status_label || "",
    nextAction: String(raw.next_action || "").toUpperCase(),
    createdAt: raw.requested_at || raw.created_at || null,
  };
}

function bucketFor(status) {
  if (["REQUESTED", "PENDING"].includes(status)) return "PENDING";
  if (status === "APPROVED") return "APPROVED";
  if (["EXECUTED", "CANCELLED", "COMPLETED"].includes(status)) return "DONE";
  if (["REJECTED", "DECLINED", "EXPIRED"].includes(status)) return "REJECTED";
  return "PENDING";
}

const STATUS_TONE = {
  PENDING: { bg: "#fff5e0", fg: "#a06a00" },
  APPROVED: { bg: "#e6f0fb", fg: "#1a4a7a" },
  DONE: { bg: "#e6f4ea", fg: C.success },
  REJECTED: { bg: "#fdecea", fg: C.danger },
};

function StatusBadge({ bucket, label }) {
  const t = STATUS_TONE[bucket] || STATUS_TONE.PENDING;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "100px", background: t.bg, color: t.fg, fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label || bucket}</span>;
}

function DecisionModal({ mode, request, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  const approving = mode === "approve";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>{approving ? "Approve" : "Reject"} request</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>{request.label} · {request.reference} · {fmtINR(request.amount)}</p>
        <label style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" }}>
          Reason<span style={{ color: C.danger }}> *</span>
        </label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={approving ? "Why is this being approved?" : "Why is this being rejected?"}
          style={{ width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button onClick={onClose} disabled={busy} style={{ flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onSubmit(reason.trim())} disabled={busy || !reason.trim()}
            style={{ flex: 2, padding: "11px", background: approving ? C.success : C.danger, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer", opacity: !reason.trim() ? 0.6 : 1 }}>
            {busy ? "Working…" : approving ? "Approve" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

const actionBtn = (bg) => ({ padding: "6px 14px", borderRadius: "7px", border: "none", background: bg, color: "#fff", fontSize: "12px", fontWeight: "700", cursor: "pointer" });

export default function IPDBillingApprovals() {
  const location = useLocation();
  const passedPatient = location.state?.patient || null;
  const { can, meetsLevel } = useBillingPermissions();

  const [activeAdmissionId, setActiveAdmissionId] = useState(() => resolveAdmissionId(passedPatient));
  const [selectedPatient, setSelectedPatient] = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions, setAdmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [filter, setFilter] = useState("PENDING");
  const [decision, setDecision] = useState(null); // { mode, request }

  const hasPatient = Boolean(activeAdmissionId);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      setAdmissions(await listAllocatedAdmissions("Allocated"));
    } catch (err) {
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => { loadAdmissions(); }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setActiveAdmissionId(id);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const refreshRequests = useCallback(async () => {
    setIsLoading(true);
    setApiError("");
    try {
      // No patient selected → tenant-wide inbox; patient selected → admission-scoped.
      const rows = await listApprovalRequests({ admissionId: activeAdmissionId || undefined, queue: "all" });
      const merged = rows
        .map((r) => normalizeApproval(r, activeAdmissionId || null))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setRequests(merged);
    } catch (err) {
      setApiError(toBillingError(err, "Failed to load approval requests"));
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeAdmissionId]);

  useEffect(() => { refreshRequests(); }, [refreshRequests]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setActiveAdmissionId(String(row.admission_id));
    setSearchQuery("");
    setApiError("");
    setApiMessage("");
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    setActiveAdmissionId("");
    clearAdmissionContext();
    setSearchQuery("");
    setApiError("");
    setApiMessage("");
  }, []);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      setApiMessage(successMsg);
      setApiError("");
      setDecision(null);
      await refreshRequests();
    } catch (err) {
      setApiError(toBillingError(err));
      setApiMessage("");
    } finally {
      setBusy(false);
    }
  };

  const submitDecision = (reason) => {
    const { mode, request } = decision;
    if (!request.meta) { setApiError("This request type can't be actioned here."); setDecision(null); return; }
    const approve = mode === "approve";
    runAction(() => request.meta.approve(request.admissionId, request.id, { approve, reason: reason || undefined }), approve ? "Request approved." : "Request rejected.");
  };

  const executeRequest = (request) => {
    if (!request.meta || !request.meta.execute) return;
    runAction(() => request.meta.execute(request.admissionId, request.id), "Request executed.");
  };

  const canApprove = (r) => {
    if (!r.meta) return false;
    // A meta with approveLevel gates on seniority instead of a permission code — see the
    // ADHOC_DISCOUNT note above.
    if (r.meta.approveLevel) return meetsLevel(r.meta.approveLevel);
    return can(r.meta.approvePerm);
  };
  const canExecute = (r) => r.meta && r.meta.execute && can(r.meta.execPerm);

  const counts = useMemo(() => requests.reduce((acc, r) => {
    const b = bucketFor(r.status);
    acc[b] = (acc[b] || 0) + 1;
    return acc;
  }, {}), [requests]);

  const visible = useMemo(
    () => requests.filter((r) => filter === "ALL" || bucketFor(r.status) === filter),
    [requests, filter]
  );

  return (
    <IPDBillingLayout activeNav="approvals">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to review approval requests"
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Approvals</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              {hasPatient
                ? `Cancellation, refund & print-reset requests · Admission ${activeAdmissionId}`
                : "All admissions · select a patient above to approve or execute"}
            </p>
            {apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{apiError}</p>}
            {apiMessage && !apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.success, fontWeight: "600" }}>{apiMessage}</p>}
          </div>
          <button onClick={refreshRequests} disabled={busy || isLoading}
            style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 16px", background: "#fff", color: C.text, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
            <SvgIcon name="refresh" size={15} color={C.text} /> Refresh
          </button>
        </div>

        {(
          <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            <div style={{ display: "flex", gap: "6px", padding: "14px 18px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
              {FILTERS.map((f) => {
                const active = filter === f.id;
                const n = f.id === "ALL" ? requests.length : counts[f.id] || 0;
                return (
                  <button key={f.id} onClick={() => setFilter(f.id)}
                    style={{ padding: "7px 14px", borderRadius: "8px", border: `1px solid ${active ? C.primary : C.border}`, background: active ? C.primary : "#fff", color: active ? "#fff" : C.text, fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                    {f.label}{n ? ` · ${n}` : ""}
                  </button>
                );
              })}
            </div>

            {isLoading ? (
              <div style={{ padding: "40px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>Loading requests…</div>
            ) : visible.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>
                No {filter === "ALL" ? "" : `${FILTERS.find((f) => f.id === filter)?.label.toLowerCase()} `}requests{hasPatient ? " for this admission" : ""}.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: C.textSub, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      <th style={{ padding: "10px 18px" }}>Admission Id</th>
                      <th style={{ padding: "10px 18px" }}>Patient</th>
                      <th style={{ padding: "10px 18px" }}>Request</th>
                      <th style={{ padding: "10px 12px" }}>Type</th>
                      <th style={{ padding: "10px 12px" }}>Reference</th>
                      <th style={{ padding: "10px 12px", textAlign: "right" }}>Amount</th>
                      <th style={{ padding: "10px 12px" }}>Reason</th>
                      <th style={{ padding: "10px 12px" }}>Status</th>
                      <th style={{ padding: "10px 18px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => {
                      const bucket = bucketFor(r.status);
                      const isPending = r.nextAction === "APPROVE_OR_REJECT" || bucket === "PENDING";
                      const isExecutable = r.nextAction === "EXECUTE" || bucket === "APPROVED";
                      return (
                        <tr key={`${r.context}-${r.id}`} style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={{ padding: "12px 18px", fontWeight: "700", color: C.text }}>{r.admissionId || "—"}</td>
                          <td style={{ padding: "12px 18px", fontWeight: "700", color: C.text }}>
                            {r.patientName || "—"}
                            {r.patientCode && <div style={{ fontSize: "10px", fontWeight: "600", color: C.textSub, marginTop: "2px" }}>{r.patientCode}</div>}
                          </td>
                          <td style={{ padding: "12px 18px", fontWeight: "700", color: C.text }}>{r.number}</td>
                          <td style={{ padding: "12px", color: C.textSub, textTransform: "capitalize" }}>{r.label}</td>
                          <td style={{ padding: "12px", color: C.text }}>
                            {r.reference}
                            {r.postCancelAction && <div style={{ fontSize: "10px", color: C.textSub, marginTop: "2px", textTransform: "lowercase" }}>{r.postCancelAction.replace(/_/g, " ")}</div>}
                          </td>
                          <td style={{ padding: "12px", textAlign: "right", fontWeight: "700", color: C.text }}>{fmtINR(r.amount)}</td>
                          <td style={{ padding: "12px", color: C.textSub, maxWidth: "200px" }}>{r.reason || "—"}</td>
                          <td style={{ padding: "12px" }}><StatusBadge bucket={bucket} label={r.uiStatusLabel} /></td>
                          <td style={{ padding: "12px 18px" }}>
                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
                              {/* Actions need an admission id (tenant-wide rows don't carry one). */}
                              {!r.admissionId && (bucket === "PENDING" || bucket === "APPROVED") ? (
                                <span style={{ fontSize: "11px", color: C.textSub }}>Open patient to act</span>
                              ) : (
                                <>
                                  {isPending && bucket === "PENDING" && canApprove(r) && (
                                    <>
                                      <button onClick={() => setDecision({ mode: "approve", request: r })} disabled={busy} style={actionBtn(C.success)}>Approve</button>
                                      <button onClick={() => setDecision({ mode: "reject", request: r })} disabled={busy} style={actionBtn(C.danger)}>Reject</button>
                                    </>
                                  )}
                                  {isExecutable && bucket === "APPROVED" && canExecute(r) && (
                                    <button onClick={() => executeRequest(r)} disabled={busy} style={actionBtn(C.primary)}>Execute</button>
                                  )}
                                  {bucket === "PENDING" && !canApprove(r) && <span style={{ fontSize: "11px", color: C.textSub }}>Awaiting approver</span>}
                                  {bucket === "APPROVED" && !canExecute(r) && (
                                    <span style={{ fontSize: "11px", color: C.textSub }}>
                                      {r.meta && !r.meta.execute ? (r.meta.postApproveHint || "Approved") : "Awaiting execution"}
                                    </span>
                                  )}
                                  {(bucket === "DONE" || bucket === "REJECTED") && <span style={{ fontSize: "11px", color: C.textSub }}>—</span>}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {decision && (
        <DecisionModal mode={decision.mode} request={decision.request} busy={busy} onClose={() => setDecision(null)} onSubmit={submitDecision} />
      )}
    </IPDBillingLayout>
  );
}
